import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableEntity, ReferenceField } from '@uncinc/react-drupal-core/types';
import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';

import type { ProfileDefault } from '../../../../types/uncinc_profile/profile';
import type { TaxonomyDepartment, TaxonomyLocation } from '../../../../types/uncinc_profile/taxonomy';
import {
  ButtonWrapper,
  ParagraphsRenderer,
  RelatedContent,
  Tags,
} from '../../../../components';
import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';
import avatar from '../../../../assets/images/avatar.svg';

import './style.scss';
import { RELATED_ARTICLES_HITS_PER_PAGE } from '../../../../config/constants';

interface ProfileDetailsProps {
  telephone?: string;
  email?: string;
  profileLocations?: ReferenceField<TaxonomyLocation>[];
  profileDepartments?: ReferenceField<TaxonomyDepartment>[];
  university?: string;
  promotor?: string;
}

const ProfileDetails: React.FC<ProfileDetailsProps> = ({
  telephone,
  email,
  profileLocations,
  profileDepartments,
  university,
  promotor,
}) => {
  const { t } = useTranslation();

  if (
    !telephone &&
    !email &&
    profileLocations?.length === 0 &&
    profileDepartments?.length === 0
  ) {
    return null;
  }

  return (
    <div className="ProfilePage__details">
      <h3 className="label--regular">{t('content_types.profile.contact_details')}</h3>
      {telephone && (
        <ButtonWrapper
          className="ProfilePage__details__link ProfilePage__details__link__phone"
          href={`tel:${telephone}`}
          type="textual"
          iconName="call"
          iconPosition="left"
        >
          {telephone}
        </ButtonWrapper>
      )}
      {email && (
        <ButtonWrapper
          className="ProfilePage__details__link ProfilePage__details__link__email"
          href={`mailto:${email}`}
          type="textual"
          iconName="mail"
          iconPosition="left"
        >
          {email}
        </ButtonWrapper>
      )}
      {(profileLocations && profileLocations.length > 0) && (
        <>
          {profileLocations.map((location) => (
            <ButtonWrapper
              className="ProfilePage__details__link ProfilePage__details__link__location"
              href={location.url}
              type="textual"
              iconName="location_on"
              iconPosition="left"
            >
              {location.entity.name[0].value}
            </ButtonWrapper>
          ))}
        </>
      )}
      {(profileDepartments && profileDepartments.length > 0) && (
        <div className="ProfilePage__details__departments">
          <Tags
            title={t('taxonomies.departments.title', { count: profileDepartments.length })}
            items={taxonomyTagEntityMapper(profileDepartments)}
          />
        </div>
      )}

      {(university || promotor) && (
        <div className="ProfilePage__details__student-details">
          {university && (
            <div>
              <p className="label--regular">{t('content_types.profile.university')}</p>
              {university}
            </div>
          )}
          {promotor && (
            <div>
              <p className="label--regular">{t('content_types.profile.promotor')}</p>
              {promotor}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ProfileProps {
  entity: ImmutableEntity<ProfileDefault>;
  className?: string;
}

const ProfilePage: React.FC<ProfileProps> = ({
  entity,
  className,
}) => {
  const classes = classNames(className, 'Node', 'ProfilePage');
  const { t } = useTranslation();

  const profile = entity.toJS();
  const profileId = profile.profile_id[0].value;
  const langcode = profile.langcode[0].value;
  const profileFirstName = profile.field_first_name[0].value;
  const profileLastName = profile.field_last_name[0]?.value;
  const fullName = [profileFirstName, profileLastName].join(' ');
  const profileDescription = profile.field_description?.[0]?.processed;
  const profileResearchInterests = profile.field_research_interests?.[0]?.processed;
  const profileAdditionalFunctions = profile.field_additional_functions?.[0]?.processed;
  const email = profile.field_email_address?.[0]?.value;
  const profileFunctions =
    taxonomyTagEntityMapper(profile.field_functions).map(
      (profileFunction) => profileFunction.name,
    ).join(', ');
  const profileLocations = profile.field_locations;
  const profileDepartments = profile.field_departments;
  const profileThemes = profile.field_themes;
  const profilePicture = profile.field_media_item[0];
  const telephone = profile.field_telephone?.[0]?.value;
  const university = profile.field_university?.[0]?.value;
  const promotor = profile.field_promotor?.[0]?.value;

  return (
    <article itemType="http://schema.org/WebPage" className={classes}>
      <div className="ProfilePage__hero">
        <Container>
          <div className="row">
            <div className="col-12">
              <BreadcrumbsV2 showHomeIcon={false} showCurrentPage={false} />
            </div>
          </div>
          <div className="row">
            <div className="ProfilePage__main-section col-12 col-md-10 col-lg-8 col-xl-7">
              <section className="ProfilePage__hero">
                <div className="ProfilePage__hero__header">
                  {profilePicture ? (
                    <div className="ProfilePage__hero__image">
                      <DynamicMediaItem
                        media={profilePicture}
                        imageAspectRatio="1_1"
                        aboveTheFold
                      />
                    </div>
                  ) : (
                    <div className="ProfilePage__hero__image">
                      <img src={avatar} alt={fullName} />
                    </div>
                  )}

                  <div className="ProfilePage__hero__header__heading">
                    <h1 itemProp="name" className="ProfilePage__hero__name">{fullName}</h1>
                    {(profileFunctions && profileFunctions.length > 0) && (
                      <div className="ProfilePage__hero__functions body--large">
                        {profileFunctions}
                      </div>
                    )}
                  </div>
                </div>

                {profileDescription && (
                  <WysiwygField
                    className="ProfilePage__hero__description body--large"
                    property="schema:text"
                    html={profileDescription}
                  />
                )}
                <div className="ProfilePage__main-section__profile-details">
                  <ProfileDetails
                    telephone={telephone}
                    email={email}
                    profileLocations={profileLocations}
                    profileDepartments={profileDepartments}
                  />
                </div>
                {profileResearchInterests && (
                  <div className="ProfilePage__hero__research-interests">
                    <h2>{t('content_types.profile.research_interests')}</h2>
                    <WysiwygField
                      property="schema:text"
                      html={profileResearchInterests}
                    />
                  </div>
                )}
                {profileAdditionalFunctions && (
                  <div className="ProfilePage__hero__additional-functions">
                    <h3>{t('content_types.profile.additional_functions')}</h3>
                    <WysiwygField
                      property="schema:text"
                      html={profileAdditionalFunctions}
                    />
                  </div>
                )}
                {(profileThemes && profileThemes.length > 0) && (
                  <div className="ProfilePage__hero__themes">
                    <h3 className="ProfilePage__hero__themes__title">
                      {t('taxonomies.themes.title_related', { count: profileThemes.length })}
                    </h3>
                    <Tags items={taxonomyTagEntityMapper(profileThemes)} />
                  </div>
                )}

                <ParagraphsRenderer className="ProfilePage__paragraphs" paragraphs={entity.get('field_paragraphs')} noBootstrap />
              </section>
            </div>

            <div className="ProfilePage__sidebar col-12 col-md-10 col-lg-4 col-xl-3 offset-xl-1">
              <section className="ProfilePage__sidebar__profile-details">
                <ProfileDetails
                  telephone={telephone}
                  email={email}
                  profileLocations={profileLocations}
                  profileDepartments={profileDepartments}
                  university={university}
                  promotor={promotor}
                />
              </section>
            </div>
          </div>
        </Container>
      </div>

      <div className="FixedReferenceBlocks">
        <Container>
          <div id="articles" className="TaxonomyDepartment__articles">
            <RelatedContent
              title={t('content_types.article.related_content')}
              contentType="article"
              hitsPerPage={RELATED_ARTICLES_HITS_PER_PAGE}
              filterName="author_id"
              filterValue={profileId}
              langcode={langcode}
            />
          </div>
        </Container>
      </div>
    </article>
  );
};

export default ProfilePage;
